// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { Box, Checkbox, Flex } from '@mantine/core';
import type { KeyCreatorParams, ValueFormatterParams } from 'ag-grid-enterprise';
import type { CustomFilterProps } from 'ag-grid-react';

import { isNotNil } from 'helpers/isNotNil';
import { strongIsEqual as isEqual } from 'helpers/strongEntries';

export type SetFilterModal<Val> =
  | {
      filterType: 'set';
      values: Val[];
      filterKey?: string;
    }
  | undefined;

export const CustomValueSetFilterComponentWrapped = <TData, Opt, Val, Context>({
  values,
  model,
  onModelChange,
  buttons,
  valueFormatter,
  labelDescriptor,
  keyCreator,
  suppressSorting,
  filterKey,
  ...props
}: CustomFilterProps<TData, Context, SetFilterModal<Val>> & CustomValueSetFilterProps<TData, Opt, Val>) => {
  const { api } = props;
  const [searchPhrase, setSearchPhrase] = useState('');
  const [selected, setSelected] = useState<Val[]>(model?.values ?? []);

  useEffect(() => {
    setSelected(model?.values ?? []);
  }, [model?.values]);

  const onChange = useCallback((value: Val) => {
    setSelected((existingSelected) => {
      const index = existingSelected.findIndex((v) => isEqual(v, value));
      if (index >= 0) {
        return [...existingSelected.slice(0, index), ...existingSelected.slice(index + 1)];
      }
      return [...existingSelected, value];
    });
  }, []);

  const applyModel = useCallback(
    (newValues: typeof selected) => {
      onModelChange(
        newValues.length > 0 && newValues.length < values.length
          ? {
              filterType: 'set',
              values: newValues,
              filterKey,
            }
          : // If no values (or all values) are selected, the model can just be `undefined`
            undefined,
      );
      api.onFilterChanged();
    },
    [api, filterKey, onModelChange, values.length],
  );

  useEffect(() => {
    // If there is no `apply` button,
    // the model needs to be applied any time `selected` changes
    if (!buttons?.includes('apply')) {
      // If nothing (or everything) is selected,
      // we only want to update the model if the model isn't already `undefined`
      if (!((selected.length === 0 || selected.length === values.length) && model === undefined)) {
        applyModel(selected);
      }
    }
  }, [applyModel, buttons, model, selected, values.length]);

  const resetModel = useCallback(() => {
    setSelected([]);
    if (model) {
      setTimeout(() => {
        onModelChange(undefined);
        api.onFilterChanged();
      });
    }
  }, [api, model, onModelChange]);

  /** The list of visible options the user can select from (filtered by search if applicable) */
  const options = useMemo(() => {
    const opts = values
      .map((value) => ({
        value: keyCreator({ value, ...props }),
        label: valueFormatter({ value, ...props }) ?? '',
        labelDescription: labelDescriptor?.({ value, ...props }) ?? null,
      }))
      .filter(({ label }) => !searchPhrase || label.toLowerCase().includes(searchPhrase.toLowerCase()));
    return suppressSorting ? opts : opts.toSorted((a, b) => a.label.localeCompare(b.label));
  }, [keyCreator, labelDescriptor, props, searchPhrase, suppressSorting, valueFormatter, values]);

  /** When the user has a selected option that is no longer visible, deselect it */
  useEffect(() => {
    const visibleSelected = selected.filter((v) => options.some(({ value }) => isEqual(v, value)));

    if (!isEqual(visibleSelected, selected)) {
      setSelected(visibleSelected);
      applyModel(visibleSelected);
    }
  }, [options, selected, applyModel]);

  return (
    <>
      <Box p="sm" pb={0}>
        <Flex direction="column" gap="md">
          <div className="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper" role="presentation">
            <input
              placeholder="Search..."
              value={searchPhrase}
              onChange={(e) => setSearchPhrase(e.currentTarget.value)}
              type="text"
              className="ag-input-field-input ag-text-field-input"
              style={{ paddingLeft: 26, minWidth: 180 }}
            />
          </div>
          <Flex
            direction="column"
            gap={8}
            style={{
              overflowY: 'auto',
              height: 144,
            }}
          >
            <Checkbox
              label="(Select all)"
              indeterminate={selected.length > 0 && selected.length !== options.length}
              checked={selected.length > 0}
              size="xs"
              styles={{ label: { fontSize: 14 } }}
              onChange={() => {
                // If anything is selected, this Checkbox deselects whatever is visible
                if (selected.length > 0) {
                  setSelected([]);
                }
                // If nothing is selected, this Checkbox selects everything visible
                else {
                  setSelected((s) => [...s, ...options.map((o) => o.value).filter(isNotNil)]);
                }
              }}
            />
            {options.map(
              ({ label, labelDescription, value }) =>
                isNotNil(value) && (
                  <Checkbox
                    key={label}
                    label={
                      <span>
                        <span>{label}</span>{' '}
                        {labelDescription && <span style={{ opacity: 0.6 }}>({labelDescription})</span>}
                      </span>
                    }
                    checked={selected.some((n) => isEqual(n, value))}
                    onChange={() => onChange(value)}
                    size="xs"
                    styles={{ label: { fontSize: 14 } }}
                  />
                ),
            )}
          </Flex>
        </Flex>
      </Box>
      <div className="ag-filter-apply-panel">
        {buttons?.includes('reset') && (
          <button
            className="ag-button ag-standard-button ag-filter-apply-panel-button"
            type="button"
            onClick={resetModel}
          >
            Reset
          </button>
        )}
        {buttons?.includes('apply') && (
          <button
            className="ag-button ag-standard-button ag-filter-apply-panel-button"
            type="button"
            onClick={() => applyModel(selected)}
          >
            Apply
          </button>
        )}
      </div>
    </>
  );
};

export type CustomValueSetFilterProps<TData, Opt, Val> = {
  /** All possible values for the filter */
  values: Opt[];
  /** Formats the value displayed in the filter dropdown */
  valueFormatter: (params: StrongOmit<ValueFormatterParams<TData, Opt>, 'node' | 'data'>) => string | null | undefined;
  labelDescriptor?: (
    params: StrongOmit<ValueFormatterParams<TData, Opt>, 'node' | 'data'>,
  ) => string | null | undefined;
  /** Generates the value to send to the backend for filtering (usually an ID) */
  keyCreator: (params: StrongOmit<KeyCreatorParams<TData, Opt>, 'node' | 'data'>) => Val | undefined;
  /** What buttons, if any, to display in the filter popup */
  buttons?: ('apply' | 'reset')[];
  /** If true, will not sort the options in the dropdown. Otherwise, will sort by the result of `valueFormatter` */
  suppressSorting?: boolean;
  filterKey?: string;
};

/**
 * A Set Filter where each value can be an object, not just a string.
 *
 * Expected to be used with `customSetFilterParams` or `filterStatusConverter`
 */
export const CustomValueSetFilterComponent = <TData, Opt, Val>(props: CustomValueSetFilterProps<TData, Opt, Val>) =>
  props.values.length === 0
    ? undefined
    : function (filterProps: CustomFilterProps<TData, never, SetFilterModal<Val>>) {
        return <CustomValueSetFilterComponentWrapped<TData, Opt, Val, never> {...filterProps} {...props} />;
      };
